import React from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import {
  Users,
  Briefcase,
  CreditCard,
  CheckCircle,
  Clock,
  TrendingUp,
  Plus,
  ArrowRight,
  Calendar,
  DollarSign,
  BarChart3,
  Target,
  ShoppingCart,
  UserCheck,
  Award,
  FileText,
  AlertCircle
} from 'lucide-react'
import { useVendorDashboard } from '../VendorDashboard'

const VendorOverview = () => {
  const navigate = useNavigate()
  const {
    vendorStatus,
    vendorData,
    resources,
    isLoading
  } = useVendorDashboard()

  // Calculate stats
  const totalResources = resources.length
  const activeResources = resources.filter(r => r.status === 'active' || r.status === 'available').length
  const onProjectResources = resources.filter(r => r.status === 'on-project' || r.status === 'assigned' || r.status === 'placed').length

  // Mock data for PO, Interviews, Placements (replace with actual API data)
  const stats = {
    pendingPOs: 3,
    totalPOs: 12,
    scheduledInterviews: 5,
    completedInterviews: 8,
    activePlacements: 7,
    totalPlacements: 15,
    totalEarnings: 1250000,
    monthlyEarnings: 185000,
    profileViews: 245,
    matchScore: 87
  }

  const quickActions = [
    {
      id: 'add-resource',
      title: 'Add Bench Resource',
      description: 'Post a new resource to your bench',
      icon: <Plus className="w-6 h-6" />,
      color: 'primary',
      action: () => navigate('/vendor/dashboard/add-bench-resource')
    },
    {
      id: 'view-resources',
      title: 'Manage Resources',
      description: 'View and manage your bench resources',
      icon: <Users className="w-6 h-6" />,
      color: 'accent',
      action: () => navigate('/vendor/dashboard/bench-resources')
    },
    {
      id: 'purchase-orders',
      title: 'Purchase Orders',
      description: 'View POs received from AJA',
      icon: <ShoppingCart className="w-6 h-6" />,
      color: 'warning',
      action: () => navigate('/vendor/dashboard/purchase-orders')
    },
    {
      id: 'interviews',
      title: 'Interviews',
      description: 'Schedule and manage interviews',
      icon: <UserCheck className="w-6 h-6" />,
      color: 'success',
      action: () => navigate('/vendor/dashboard/interviews')
    }
  ]

  // Sales/Earnings data for charts
  const monthlyEarnings = [
    { month: 'Jan', earnings: 145000 },
    { month: 'Feb', earnings: 162000 },
    { month: 'Mar', earnings: 148000 },
    { month: 'Apr', earnings: 171000 },
    { month: 'May', earnings: 165000 },
    { month: 'Jun', earnings: 185000 }
  ]

  const maxEarnings = Math.max(...monthlyEarnings.map(m => m.earnings))

  // Resource distribution for pie chart
  const resourceDistribution = [
    { label: 'Available', value: activeResources, color: 'success' },
    { label: 'Placed', value: onProjectResources, color: 'accent' },
    { label: 'Inactive', value: totalResources - activeResources - onProjectResources, color: 'secondary' }
  ].filter(item => item.value > 0)

  const totalResourcesForChart = resourceDistribution.reduce((sum, item) => sum + item.value, 0)

  // Calculate pie chart angles
  let currentAngle = -90
  const pieData = resourceDistribution.map((item) => {
    const percentage = (item.value / totalResourcesForChart) * 100
    const angle = (item.value / totalResourcesForChart) * 360
    const startAngle = currentAngle
    currentAngle += angle
    return {
      ...item,
      percentage,
      startAngle,
      angle
    }
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600"></div>
          <p className="text-secondary-600 dark:text-secondary-400">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-primary-600 via-primary-500 to-accent-600 rounded-xl p-6 md:p-8 text-white shadow-lg"
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold mb-2">
              Welcome back, {vendorData.companyName || 'Vendor'}!
            </h1>
            <p className="text-primary-100 text-sm md:text-base mb-4">
              {vendorStatus === 'active'
                ? 'Your account is active. Manage your resources, handle purchase orders, and grow your business through AJA Bench.'
                : 'Complete your verification to unlock all features and start receiving purchase orders from companies.'}
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                {vendorStatus === 'active' ? (
                  <CheckCircle className="w-5 h-5 text-success-300" />
                ) : (
                  <Clock className="w-5 h-5 text-warning-300" />
                )}
                <span className="text-sm font-medium">
                  Status: {vendorStatus === 'active' ? 'Active' : 'Pending Verification'}
                </span>
              </div>
              {vendorData.subscriptionPlan && (
                <div className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-primary-200" />
                  <span className="text-sm">Plan: {vendorData.subscriptionPlan}</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex-shrink-0 flex gap-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
              <div className="text-center">
                <div className="text-3xl font-bold mb-1">{totalResources}</div>
                <div className="text-sm text-primary-100">Total Resources</div>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
              <div className="text-center">
                <div className="text-3xl font-bold mb-1">{stats.activePlacements}</div>
                <div className="text-sm text-primary-100">Active Placements</div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card p-6 shadow-glow hover:shadow-xl transition-shadow"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <TrendingUp className="w-5 h-5 text-success-600" />
          </div>
          <h3 className="text-sm font-medium text-secondary-600 dark:text-secondary-400 mb-1">
            Bench Resources
          </h3>
          <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-100 mb-1">
            {totalResources}
          </p>
          <p className="text-xs text-secondary-500 dark:text-secondary-400">
            {activeResources} available • {onProjectResources} placed
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card p-6 shadow-glow hover:shadow-xl transition-shadow"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-warning-100 dark:bg-warning-900/30 rounded-lg flex items-center justify-center">
              <ShoppingCart className="w-6 h-6 text-warning-600 dark:text-warning-400" />
            </div>
            {stats.pendingPOs > 0 && (
              <div className="w-6 h-6 bg-error-500 rounded-full flex items-center justify-center">
                <span className="text-xs font-bold text-white">{stats.pendingPOs}</span>
              </div>
            )}
          </div>
          <h3 className="text-sm font-medium text-secondary-600 dark:text-secondary-400 mb-1">
            Purchase Orders
          </h3>
          <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-100 mb-1">
            {stats.totalPOs}
          </p>
          <p className="text-xs text-secondary-500 dark:text-secondary-400">
            {stats.pendingPOs} pending • {stats.totalPOs - stats.pendingPOs} processed
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card p-6 shadow-glow hover:shadow-xl transition-shadow"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-accent-100 dark:bg-accent-900/30 rounded-lg flex items-center justify-center">
              <UserCheck className="w-6 h-6 text-accent-600 dark:text-accent-400" />
            </div>
            {stats.scheduledInterviews > 0 && (
              <Clock className="w-5 h-5 text-warning-600" />
            )}
          </div>
          <h3 className="text-sm font-medium text-secondary-600 dark:text-secondary-400 mb-1">
            Interviews
          </h3>
          <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-100 mb-1">
            {stats.scheduledInterviews + stats.completedInterviews}
          </p>
          <p className="text-xs text-secondary-500 dark:text-secondary-400">
            {stats.scheduledInterviews} scheduled • {stats.completedInterviews} completed
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card p-6 shadow-glow hover:shadow-xl transition-shadow"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-success-100 dark:bg-success-900/30 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-success-600 dark:text-success-400" />
            </div>
            <TrendingUp className="w-5 h-5 text-success-600" />
          </div>
          <h3 className="text-sm font-medium text-secondary-600 dark:text-secondary-400 mb-1">
            Total Earnings
          </h3>
          <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-100 mb-1">
            ₹{stats.totalEarnings.toLocaleString()}
          </p>
          <p className="text-xs text-secondary-500 dark:text-secondary-400">
            ₹{stats.monthlyEarnings.toLocaleString()} this month
          </p>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-secondary-900 dark:text-secondary-100">
            Quick Actions
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions.map((action, index) => (
            <motion.button
              key={action.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * index }}
              onClick={action.action}
              className={`card p-5 shadow-glow hover:shadow-xl transition-all text-left group cursor-pointer border-2 border-transparent hover:border-primary-200 dark:hover:border-primary-800`}
            >
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-3 ${
                action.color === 'primary' ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400' :
                action.color === 'accent' ? 'bg-accent-100 dark:bg-accent-900/30 text-accent-600 dark:text-accent-400' :
                action.color === 'success' ? 'bg-success-100 dark:bg-success-900/30 text-success-600 dark:text-success-400' :
                'bg-warning-100 dark:bg-warning-900/30 text-warning-600 dark:text-warning-400'
              }`}>
                {action.icon}
              </div>
              <h3 className="font-semibold text-secondary-900 dark:text-secondary-100 mb-1 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                {action.title}
              </h3>
              <p className="text-xs text-secondary-600 dark:text-secondary-400">
                {action.description}
              </p>
              <ArrowRight className="w-4 h-4 text-secondary-400 mt-2 group-hover:text-primary-600 group-hover:translate-x-1 transition-all" />
            </motion.button>
          ))}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sales & Earnings Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="lg:col-span-2 card p-6 shadow-glow"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-bold text-secondary-900 dark:text-secondary-100">
                Earnings Overview
              </h2>
              <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">
                Monthly earnings from placements
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-secondary-900 dark:text-secondary-100">
                ₹{stats.monthlyEarnings.toLocaleString()}
              </span>
              <span className="text-xs text-success-600 dark:text-success-400 flex items-center gap-1">
                <TrendingUp className="w-3 h-3" />
                +12%
              </span>
            </div>
          </div>
          
          {/* Bar Chart */}
          <div className="relative h-64">
            <svg className="w-full h-full" viewBox="0 0 600 200" preserveAspectRatio="none">
              {/* Grid lines */}
              {[0, 25, 50, 75, 100].map((percent) => (
                <line
                  key={percent}
                  x1="0"
                  y1={`${percent}%`}
                  x2="100%"
                  y2={`${percent}%`}
                  stroke="currentColor"
                  strokeWidth="1"
                  className="text-secondary-200 dark:text-secondary-700"
                  strokeDasharray="4 4"
                />
              ))}
              
              {/* Bars */}
              {monthlyEarnings.map((data, index) => {
                const barHeight = (data.earnings / maxEarnings) * 100
                const barWidth = 80
                const spacing = 20
                const x = (index * (barWidth + spacing)) + 40
                const y = 180 - (barHeight * 1.6)
                
                return (
                  <g key={data.month}>
                    <rect
                      x={x}
                      y={y}
                      width={barWidth}
                      height={180 - y}
                      rx="4"
                      className="fill-primary-500 hover:fill-primary-600 transition-colors"
                    />
                    <text
                      x={x + barWidth / 2}
                      y="195"
                      textAnchor="middle"
                      className="text-xs fill-secondary-600 dark:fill-secondary-400"
                    >
                      {data.month}
                    </text>
                    <text
                      x={x + barWidth / 2}
                      y={y - 5}
                      textAnchor="middle"
                      className="text-xs font-semibold fill-secondary-900 dark:fill-secondary-100"
                    >
                      ₹{(data.earnings / 1000).toFixed(0)}k
                    </text>
                  </g>
                )
              })}
            </svg>
            
            {/* Y-axis labels */}
            <div className="absolute left-0 top-0 h-full flex flex-col justify-between text-xs text-secondary-500 dark:text-secondary-400 pr-2">
              <span>₹{(maxEarnings / 1000).toFixed(0)}k</span>
              <span>₹{((maxEarnings * 0.75) / 1000).toFixed(0)}k</span>
              <span>₹{((maxEarnings * 0.5) / 1000).toFixed(0)}k</span>
              <span>₹{((maxEarnings * 0.25) / 1000).toFixed(0)}k</span>
              <span>₹0</span>
            </div>
          </div>

          {/* Chart Legend */}
          <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-secondary-200 dark:border-secondary-700">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-primary-500"></div>
              <span className="text-xs text-secondary-600 dark:text-secondary-400">Monthly Earnings</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-success-500"></div>
              <span className="text-xs text-secondary-600 dark:text-secondary-400">Average: ₹{Math.round(monthlyEarnings.reduce((sum, m) => sum + m.earnings, 0) / monthlyEarnings.length / 1000)}k</span>
            </div>
          </div>
        </motion.div>

        {/* Right Column - Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="space-y-6"
        >
          {/* Purchase Orders Summary */}
          <div className="card p-6 shadow-glow">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-secondary-900 dark:text-secondary-100">
                Purchase Orders
              </h2>
              {stats.pendingPOs > 0 && (
                <span className="px-2 py-1 bg-error-100 text-error-700 dark:bg-error-900/30 dark:text-error-300 rounded-full text-xs font-semibold">
                  {stats.pendingPOs} New
                </span>
              )}
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-secondary-600 dark:text-secondary-400">Pending</span>
                <span className="text-lg font-bold text-warning-600">{stats.pendingPOs}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-secondary-600 dark:text-secondary-400">Total</span>
                <span className="text-sm font-semibold text-secondary-900 dark:text-secondary-100">{stats.totalPOs}</span>
              </div>
            </div>
            <button
              onClick={() => navigate('/vendor/dashboard/purchase-orders')}
              className="w-full mt-4 btn-outline text-xs font-semibold py-2"
            >
              View All POs
            </button>
          </div>

          {/* Interviews Summary */}
          <div className="card p-6 shadow-glow">
            <h2 className="text-lg font-bold text-secondary-900 dark:text-secondary-100 mb-4">
              Interviews
            </h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-secondary-600 dark:text-secondary-400">Scheduled</span>
                <span className="text-lg font-bold text-accent-600">{stats.scheduledInterviews}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-secondary-600 dark:text-secondary-400">Completed</span>
                <span className="text-sm font-semibold text-secondary-900 dark:text-secondary-100">{stats.completedInterviews}</span>
              </div>
            </div>
            <button
              onClick={() => navigate('/vendor/dashboard/interviews')}
              className="w-full mt-4 btn-outline text-xs font-semibold py-2"
            >
              Manage Interviews
            </button>
          </div>

          {/* Placements Summary */}
          <div className="card p-6 shadow-glow">
            <h2 className="text-lg font-bold text-secondary-900 dark:text-secondary-100 mb-4">
              Placements
            </h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-secondary-600 dark:text-secondary-400">Active</span>
                <span className="text-lg font-bold text-success-600">{stats.activePlacements}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-secondary-600 dark:text-secondary-400">Total</span>
                <span className="text-sm font-semibold text-secondary-900 dark:text-secondary-100">{stats.totalPlacements}</span>
              </div>
            </div>
            <button
              onClick={() => navigate('/vendor/dashboard/placements')}
              className="w-full mt-4 btn-outline text-xs font-semibold py-2"
            >
              View Placements
            </button>
          </div>
        </motion.div>
      </div>

      {/* Resource Distribution & Workflow Status */}
      {totalResources > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Resource Distribution Pie Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="card p-6 shadow-glow"
          >
            <h2 className="text-lg font-bold text-secondary-900 dark:text-secondary-100 mb-4">
              Resource Distribution
            </h2>
            <div className="flex items-center justify-center mb-6">
              <svg width="180" height="180" viewBox="0 0 180 180" className="transform -rotate-90">
                <circle
                  cx="90"
                  cy="90"
                  r="70"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="40"
                  className="text-secondary-200 dark:text-secondary-700"
                />
                {pieData.map((item, index) => {
                  const radius = 70
                  const startAngleRad = (item.startAngle * Math.PI) / 180
                  const endAngleRad = ((item.startAngle + item.angle) * Math.PI) / 180
                  const x1 = 90 + radius * Math.cos(startAngleRad)
                  const y1 = 90 + radius * Math.sin(startAngleRad)
                  const x2 = 90 + radius * Math.cos(endAngleRad)
                  const y2 = 90 + radius * Math.sin(endAngleRad)
                  const largeArcFlag = item.angle > 180 ? 1 : 0
                  
                  const colorClass = item.color === 'success' 
                    ? 'text-success-500' 
                    : item.color === 'accent'
                    ? 'text-accent-500'
                    : 'text-secondary-400'
                  
                  return (
                    <path
                      key={index}
                      d={`M 90 90 L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`}
                      className={`fill-current ${colorClass} transition-all hover:opacity-80`}
                    />
                  )
                })}
              </svg>
            </div>
            <div className="space-y-3">
              {pieData.map((item, index) => {
                const colorClass = item.color === 'success'
                  ? 'bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300'
                  : item.color === 'accent'
                  ? 'bg-accent-100 dark:bg-accent-900/30 text-accent-700 dark:text-accent-300'
                  : 'bg-secondary-100 dark:bg-secondary-700 text-secondary-700 dark:text-secondary-300'
                
                return (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${colorClass.replace('bg-', 'bg-').replace('text-', '')}`}></div>
                      <span className="text-sm text-secondary-600 dark:text-secondary-400">{item.label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-secondary-900 dark:text-secondary-100">{item.value}</span>
                      <span className="text-xs text-secondary-500 dark:text-secondary-400">({item.percentage.toFixed(1)}%)</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </motion.div>

          {/* Workflow Status */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="card p-6 shadow-glow"
          >
            <h2 className="text-lg font-bold text-secondary-900 dark:text-secondary-100 mb-4">
              Workflow Status
            </h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Users className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-secondary-900 dark:text-secondary-100">Resources Posted</p>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">
                    {totalResources} resources available for companies to view
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-warning-100 dark:bg-warning-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                  <ShoppingCart className="w-4 h-4 text-warning-600 dark:text-warning-400" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-secondary-900 dark:text-secondary-100">PO Received</p>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">
                    {stats.pendingPOs} pending POs from AJA managers
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-accent-100 dark:bg-accent-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                  <UserCheck className="w-4 h-4 text-accent-600 dark:text-accent-400" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-secondary-900 dark:text-secondary-100">Interviews</p>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">
                    {stats.scheduledInterviews} interviews scheduled with companies
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-success-100 dark:bg-success-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-4 h-4 text-success-600 dark:text-success-400" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-secondary-900 dark:text-secondary-100">Placements</p>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">
                    {stats.activePlacements} resources currently placed with companies
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}

export default VendorOverview
